// ─── Article registry ────────────────────────────────────────
// Single source of truth for every article on the site.
// Drop any .md file into src/articles/ and it auto-appears here.
// Sorted newest-first via the dateSort field in frontmatter.
//
// Extracted from Home/Home.tsx so the home page (the scroll-world film),
// the articles index, ArticlePage, and HeaderSearch can all read the same
// registry without importing a page component.
// ─────────────────────────────────────────────────────────────

export interface Article {
    slug: string;
    title: string;
    date: string;
    dateSort: string;
    category: string;
    summary: string;
    cover: string;
    author: string;
    content: string;
    pinned?: boolean;
    demo?: string;  // e.g. "image-classifier" — triggers live demo widget in ArticlePage
}

const mdCtx = require.context('!!raw-loader!../../articles', false, /\.md$/);

function parseFrontmatter(raw: string): { meta: Record<string, string>; content: string } {
    const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
    if (!match) return { meta: {}, content: raw };
    const meta: Record<string, string> = {};
    match[1].split(/\r?\n/).forEach(line => {
        const idx = line.indexOf(':');
        if (idx === -1) return;
        const key = line.slice(0, idx).trim();
        const val = line.slice(idx + 1).trim().replace(/^"|"$/g, '');
        meta[key] = val;
    });
    return { meta, content: match[2].trim() };
}

// Some early articles carry a month-only dateSort ("2026-03"). Padding to a full
// date keeps the string comparison below honest — without it, "2026-03" and
// "2026-03-15" order by prefix length rather than by time.
function sortKey(dateSort: string): string {
    if (/^\d{4}-\d{2}$/.test(dateSort)) return `${dateSort}-01`;
    if (/^\d{4}$/.test(dateSort)) return `${dateSort}-01-01`;
    return dateSort;
}

export const allArticles: Article[] = mdCtx.keys().map((key: string) => {
    const raw: string = (mdCtx(key) as any).default ?? (mdCtx(key) as any);
    const slug = key.replace('./', '').replace('.md', '');
    const { meta, content } = parseFrontmatter(raw);
    return {
        slug,
        title: meta.title || slug,
        date: meta.date || '',
        dateSort: meta.dateSort || meta.date || '',
        category: meta.category || 'General',
        summary: meta.summary || '',
        cover: meta.cover || '',
        author: meta.author || 'Abhijith Eanuga',
        content,
        pinned: meta.pinned === 'true',
        demo: meta.demo || undefined,
    };
}).sort((a: Article, b: Article) => sortKey(b.dateSort).localeCompare(sortKey(a.dateSort)));

export const allCategories = ['All', ...Array.from(new Set(allArticles.map((a: Article) => a.category)))];

// Brand/semantic colours per topic — readable on #f0f1f6 background
export const TOPIC_COLORS: Record<string, string> = {
    AI: '#7c3aed',        // deep violet — intelligence, premium
    Salesforce: '#00A1E0', // exact Salesforce brand blue
    LLMs: '#e11d48',      // rose red — bold, research-grade
    Agents: '#f97316',    // orange — autonomous, energetic
    RAG: '#0891b2',       // teal — precise, data-driven
    LoRA: '#ca8a04',      // amber — research, discovery
};

export const ARTICLES_PER_PAGE = 6;
